import React, { useEffect, useState } from "react";
import { Hourglass } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui-kit";
import { usePrefs } from "@/contexts/PrefsContext";
import { fmtDateTime } from "@/lib/time";
import type { Booking } from "@/types";

const TONE: Record<string, string> = {
  confirmed: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  checked_in: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  attended: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  completed: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  pending: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  expired: "bg-zinc-500/20 text-zinc-700 dark:text-zinc-300",
  unable_to_contact: "bg-zinc-500/20 text-zinc-700 dark:text-zinc-300",
};

export const bookingState = (b: Booking) => (b.checked_in && b.status === "confirmed" ? "checked_in" : b.status);

export const BookingStatusPill = ({ b }: { b: Booking }) => {
  const { t } = usePrefs();
  const s = bookingState(b);
  return <span data-testid={`booking-status-${s}`} className={`inline-flex shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold ${TONE[s] || "bg-rose-500/15 text-rose-600 dark:text-rose-400"}`}>{t(`bst.${s}`)}</span>;
};

const useMinutesLeft = (iso?: string) => {
  const calc = () => (iso ? Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 60000)) : 0);
  const [m, setM] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setM(calc()), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iso]);
  return m;
};

export const DeadlineNote = ({ b, venue = false }: { b: Booking; venue?: boolean }) => {
  const { t, lang } = usePrefs();
  const left = useMinutesLeft(b.confirm_deadline);
  if (b.status !== "pending" || !b.confirm_deadline) return null;
  return (
    <p data-testid={`booking-deadline-${b.booking_id}`} className="text-xs font-semibold text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
      <Hourglass className="h-3.5 w-3.5" />
      {venue ? t("b5.timeLeft").replace("{m}", String(left)) : t("b5.deadline").replace("{time}", fmtDateTime(b.confirm_deadline, lang, { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" }))}
    </p>
  );
};

export const ReasonDialog = ({ title, note, required = true, danger = true, onSubmit, onClose, testid, presets }: {
  title: string; note?: React.ReactNode; required?: boolean; danger?: boolean; onSubmit: (reason: string) => Promise<void> | void; onClose: () => void; testid: string; presets?: string[];
}) => {
  const { t } = usePrefs();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = !required || reason.trim().length >= 3;
  const submit = async () => {
    setBusy(true);
    try { await onSubmit(reason.trim()); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-[calc(100vw-2rem)] sm:max-w-sm" data-testid={testid}>
        <DialogHeader className="text-start"><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        {note && <div className="text-sm text-muted-foreground">{note}</div>}
        {!!presets?.length && (
          <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-presets`}>
            {presets.map((p, i) => (
              <button key={i} type="button" onClick={() => setReason(p)} data-testid={`${testid}-preset-${i}`} aria-pressed={reason === p}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold text-start active:scale-95 transition-[transform,background-color] ${reason === p ? "bg-primary text-primary-foreground" : "bg-accent hover:bg-primary/15"}`}>{p}</button>
            ))}
          </div>
        )}
        <label className="text-sm font-bold">{t("b5.reason")} {!required && <span className="font-normal text-muted-foreground">{t("b5.optional")}</span>}</label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} rows={3} placeholder={t("b5.reasonPh")}
          data-testid={`${testid}-reason`} className="w-full rounded-2xl border bg-background p-3 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} data-testid={`${testid}-back`} className="h-11 rounded-full bg-accent font-bold text-sm">{t("b5.keep")}</button>
          <button onClick={submit} disabled={!ok || busy} data-testid={`${testid}-submit`}
            className={`h-11 rounded-full font-bold text-sm disabled:opacity-50 ${danger ? "bg-primary text-primary-foreground" : "bg-foreground text-background"}`}>
            {busy ? t("common.loading") : t("b5.send")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
